import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import useStore from "../../store/useStore";
import gamrentLogo from "../../assets/gamrent-logo.jpg";
import { resolveAssetUrl } from "../../utils/assetUrl";
import { getMenuItems } from "./appMenu";

const Sidebar = ({ isOpen, closeSidebar, immediate = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, authRole } = useStore();
  const role = authRole || user?.role;
  const companyLogoUrl = resolveAssetUrl(user?.companyLogo);

  const filteredMenuItems = getMenuItems(role);

  const handleNavigation = (path) => {
    navigate(path);
    closeSidebar?.();
  };

  const isActive = (path) => location.pathname === path;

  useEffect(() => {
    if (!isOpen) return undefined;
    if (!window.matchMedia("(max-width: 1023px)").matches) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") closeSidebar?.();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, closeSidebar]);

  return (
    <>
      {/* Overlay for mobile */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={closeSidebar}
        aria-hidden="true"
      ></div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-[min(18rem,88vw)] lg:z-30 lg:w-64 bg-gradient-to-b from-white via-teal-50 to-teal-100 border-r border-teal-100 shadow-2xl lg:shadow-none transform ${
          immediate ? "" : "transition-transform duration-300 ease-in-out"
        } ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
        aria-label="Navigation menu"
      >
        <div className="flex flex-col h-full pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
          {/* Logo Section */}
          <div className="flex items-center justify-between gap-3 px-5 py-4 lg:p-6 border-b border-teal-100 bg-white/60 backdrop-blur">
            <div className="flex min-w-0 items-center gap-3">
              <div className="w-10 h-10 shrink-0 rounded-xl overflow-hidden flex items-center justify-center shadow-sm border border-teal-100 bg-white">
                <img
                  src={companyLogoUrl || gamrentLogo}
                  alt={user?.companyName || "Company logo"}
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="min-w-0 leading-tight">
                <p className="system-logo-title truncate text-sm text-gray-900">{role === "SuperAdmin" ? "Gamrent" : (user?.companyName || "Gamrent")}</p>
              </div>
            </div>
            <button
              onClick={closeSidebar}
              aria-label="Close menu"
              className="lg:hidden shrink-0 p-2.5 -mr-1 hover:bg-teal-100/60 rounded-xl transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Navigation Menu */}
          <nav data-guide-nav className="flex-1 overflow-y-auto overscroll-contain py-3 lg:py-4 px-3">
            <ul className="space-y-1">
              {filteredMenuItems.map((item) => (
                <li key={item.path}>
                  <button
                    data-guide={item.path}
                    onClick={() => handleNavigation(item.path)}
                    aria-current={isActive(item.path) ? "page" : undefined}
                    className={`w-full min-h-[44px] flex items-center gap-3 px-4 py-2.5 rounded-xl text-left transition-all duration-200 ${
                      isActive(item.path)
                        ? "bg-teal-600 text-white font-semibold shadow-sm"
                        : "text-gray-700 hover:bg-teal-50 hover:text-teal-700"
                    }`}
                  >
                    <item.icon className="text-[20px]" />
                    <span className="system-menu-label">{item.label}</span>
                    {isActive(item.path) && (
                      <div className="ml-auto w-1 h-6 bg-white rounded-full"></div>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          {/* User Section */}
          <div className="border-t border-teal-100 p-3 lg:p-4 bg-white/50">
            <button
              onClick={() => handleNavigation('/profile')}
              className="w-full flex items-center gap-3 px-3 py-3 rounded-xl hover:bg-teal-50 transition-colors"
            >
              <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-teal-600 to-teal-700 rounded-full flex items-center justify-center text-white font-semibold shadow-sm">
                {user?.firstName?.[0]}{user?.lastName?.[0]}
              </div>
              <div className="min-w-0 flex-1 text-left">
                <div className="truncate text-sm font-semibold text-gray-900">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="text-xs text-gray-500">{role}</div>
              </div>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;